/**
 * Pure helpers for the broadcast banner inside the FeedbackWidget.
 * Lives next to FeedbackWidget.tsx because consuming apps copy this
 * directory verbatim. No React imports — keeps the unit tests trivial.
 *
 * Phase 17 Plan-2: read /broadcasts/active, dismissibility, anti-nag.
 */

export type BroadcastSeverity = "info" | "warning" | "critical";

export type Broadcast = {
  id: number;
  title: string;
  body: string;
  severity: BroadcastSeverity;
  ends_at: string;
};

export const DISMISSED_KEY = "180d-feedback-dismissed-broadcasts";
export const DISMISS_COUNTER_KEY = "180d-feedback-dismiss-counter";
export const MAX_DISMISSALS_BEFORE_SUPPRESS = 3;

const VALID_SEVERITIES: ReadonlySet<BroadcastSeverity> = new Set(["info", "warning", "critical"]);

/**
 * Parse the /broadcasts/active JSON response into a typed list. Drops
 * any item that doesn't conform — defensive against future API shape
 * changes or partial responses.
 */
export function parseBroadcastsResponse(raw: unknown): Broadcast[] {
  if (!raw || typeof raw !== "object") return [];
  const obj = raw as { broadcasts?: unknown };
  if (!Array.isArray(obj.broadcasts)) return [];
  const out: Broadcast[] = [];
  for (const item of obj.broadcasts) {
    if (!item || typeof item !== "object") continue;
    const b = item as Record<string, unknown>;
    if (
      typeof b.id === "number" &&
      typeof b.title === "string" &&
      typeof b.body === "string" &&
      typeof b.severity === "string" &&
      VALID_SEVERITIES.has(b.severity as BroadcastSeverity) &&
      typeof b.ends_at === "string"
    ) {
      out.push({
        id: b.id,
        title: b.title,
        body: b.body,
        severity: b.severity as BroadcastSeverity,
        ends_at: b.ends_at,
      });
    }
  }
  return out;
}

/**
 * Read the per-session dismissed-ids set from sessionStorage. Returns
 * an empty set on any error (storage blocked, malformed value).
 */
export function readDismissed(
  storage: Pick<Storage, "getItem"> | null,
): Set<number> {
  if (!storage) return new Set();
  try {
    const raw = storage.getItem(DISMISSED_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return new Set(parsed.filter((x): x is number => typeof x === "number"));
    }
    return new Set();
  } catch {
    return new Set();
  }
}

/**
 * Persist the dismissed-ids set. Swallow errors so a storage failure
 * never crashes the widget.
 */
export function writeDismissed(
  storage: Pick<Storage, "setItem"> | null,
  ids: Set<number>,
): boolean {
  if (!storage) return false;
  try {
    storage.setItem(DISMISSED_KEY, JSON.stringify([...ids]));
    return true;
  } catch {
    return false;
  }
}

/**
 * Anti-nag counter: 3 dismissals in a session suppresses non-critical
 * banners for the rest of the session. critical broadcasts always show.
 */
export function readDismissCounter(storage: Pick<Storage, "getItem"> | null): number {
  if (!storage) return 0;
  try {
    const raw = storage.getItem(DISMISS_COUNTER_KEY);
    const n = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
}

export function writeDismissCounter(
  storage: Pick<Storage, "setItem"> | null,
  n: number,
): void {
  if (!storage) return;
  try {
    storage.setItem(DISMISS_COUNTER_KEY, String(n));
  } catch {
    // ignore
  }
}

/**
 * Filter broadcasts down to what the user should actually see right now.
 * Rules:
 *  - critical broadcasts ALWAYS shown (cannot be dismissed, cannot be nag-suppressed)
 *  - non-critical broadcasts filtered through the dismissed set
 *  - if dismissCounter >= MAX_DISMISSALS_BEFORE_SUPPRESS, hide all non-critical too
 */
export function filterVisible(
  broadcasts: Broadcast[],
  dismissed: Set<number>,
  dismissCounter: number,
): Broadcast[] {
  return broadcasts.filter((b) => {
    if (b.severity === "critical") return true;
    if (dismissed.has(b.id)) return false;
    if (dismissCounter >= MAX_DISMISSALS_BEFORE_SUPPRESS) return false;
    return true;
  });
}

/**
 * UI colour mapping for a severity. Plain CSS values so the widget can
 * inline them without a Tailwind compile step.
 */
export function colourForSeverity(severity: BroadcastSeverity): {
  bg: string;
  fg: string;
  border: string;
} {
  switch (severity) {
    case "critical":
      return { bg: "#7f1d1d", fg: "#fef2f2", border: "#991b1b" };
    case "warning":
      return { bg: "#fef3c7", fg: "#78350f", border: "#fbbf24" };
    case "info":
    default:
      return { bg: "#dbeafe", fg: "#1e3a8a", border: "#60a5fa" };
  }
}

/**
 * Simple TTL cache for the /broadcasts/active response. 60-second TTL
 * matches the back-end's edge cache.
 */
type CacheEntry<T> = { at: number; value: T };
const cache = new Map<string, CacheEntry<unknown>>();
const CACHE_TTL_MS = 60_000;

export function cacheGet<T>(key: string, now: number = Date.now()): T | null {
  const entry = cache.get(key);
  if (!entry) return null;
  if (now - entry.at > CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }
  return entry.value as T;
}

export function cacheSet<T>(key: string, value: T, now: number = Date.now()): void {
  cache.set(key, { at: now, value });
}

// For testing only — clears the in-memory cache.
export function _clearCacheForTests(): void {
  cache.clear();
}
